import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { MapPin, ArrowLeft } from 'lucide-react'
import MapComponent from '../components/MapComponent'
import { useUser } from '../contexts/UserContext'
import { updateVolunteerHomeLocation } from '../api/volunteers'
import { hasValidCoordinates } from '../utils/geo'
import { formatGeocodableAddress, geocodeAddress } from '../utils/geocoding'

const NearbyMapPage = () => {
  const { user, refreshUser } = useUser()
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(
    null
  )
  const [status, setStatus] = useState<string | null>(null)
  const [locating, setLocating] = useState(false)

  useEffect(() => {
    const latitude = user?.homeLatitude
    const longitude = user?.homeLongitude
    if (hasValidCoordinates(latitude, longitude)) {
      setUserLocation({
        latitude,
        longitude: longitude as number
      })
      return
    }

    if (!user?.street || !user?.city || !user?.state) {
      setUserLocation(null)
      return
    }

    let cancelled = false
    geocodeAddress(formatGeocodableAddress(user)).then((coordinates) => {
      if (!cancelled) setUserLocation(coordinates)
    })
    return () => {
      cancelled = true
    }
  }, [
    user?.homeLatitude,
    user?.homeLongitude,
    user?.street,
    user?.city,
    user?.state,
    user?.zipCode
  ])

  const locateMe = (saveForVolunteer = false) => {
    if (!navigator.geolocation) {
      setStatus('Geolocation is not supported by your browser.')
      return
    }
    setLocating(true)
    setStatus(null)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const next = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude
        }
        setUserLocation(next)
        try {
          if (saveForVolunteer && user?.userType === 'VOLUNTEER') {
            await updateVolunteerHomeLocation(next.latitude, next.longitude)
            await refreshUser()
            setStatus('Showing your current location (also saved as home for distance ranking).')
          } else {
            setStatus('Showing your current location on the map.')
          }
        } catch (e: any) {
          setStatus(e?.message || 'Location shown, but could not save it to your profile.')
        } finally {
          setLocating(false)
        }
      },
      () => {
        setLocating(false)
        setStatus('Unable to get your location. Please allow location access and try again.')
      },
      { enableHighAccuracy: true, timeout: 12000 }
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <Link
              to="/food-banks"
              className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to food banks
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Nearby map</h1>
            <p className="text-gray-600 mt-1">
              See your saved location next to nearby food banks and suppliers.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => locateMe(false)}
              disabled={locating}
              className="btn-outline disabled:opacity-60 inline-flex items-center"
            >
              <MapPin className="h-4 w-4 mr-2" />
              {locating ? 'Locating…' : 'Use current location'}
            </button>
            {user?.userType === 'VOLUNTEER' && (
              <button
                type="button"
                onClick={() => locateMe(true)}
                disabled={locating}
                className="btn-primary disabled:opacity-60"
              >
                Locate & save as home
              </button>
            )}
          </div>
        </div>

        {status && <p className="mb-4 text-sm text-gray-700">{status}</p>}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4 flex flex-wrap gap-4 text-sm text-gray-700">
          <span className="inline-flex items-center gap-2">
            <MapPin className="h-4 w-4 text-orange-600" fill="currentColor" /> You
          </span>
          <span className="inline-flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary-600" fill="currentColor" /> Food banks
          </span>
          <span className="inline-flex items-center gap-2">
            <MapPin className="h-4 w-4 text-secondary-600" fill="currentColor" /> Suppliers
          </span>
        </div>

        {!userLocation && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950">
            Tap <strong>Use current location</strong> to place yourself on the map. Without a location,
            the map still shows food banks and suppliers in the Triangle area.
          </div>
        )}

        <MapComponent
          height="h-[70vh] min-h-[420px]"
          showControls
          showFilters
          userLocation={userLocation}
        />
      </div>
    </div>
  )
}

export default NearbyMapPage
